import React, { useEffect, useState } from 'react';
import { Instagram } from 'lucide-react';
import GreenButton from './GreenButton';
import Reveal from './Reveal';

const INSTAGRAM_URL = 'https://www.instagram.com/outpostcustom/';
// Behold free plan: latest six posts, refreshed about once a day.
const FEED_URL = 'https://feeds.behold.so/PhIoS03zwJCA46YjRlos';
const CACHE_KEY = 'oc_insta_feed';

interface FeedPost {
  id: string;
  href: string;
  image: string;
  alt: string;
}

// Shown until the feed answers, and kept if it fails.
const fallbackPosts: FeedPost[] = ['01', '02', '03', '04', '05', '06'].map((id) => ({
  id,
  href: INSTAGRAM_URL,
  image: `/home/insta/${id}.webp`,
  alt: 'Outpost Custom Instagram post',
}));

interface BeholdPost {
  id: string;
  permalink?: string;
  prunedCaption?: string;
  mediaUrl?: string;
  thumbnailUrl?: string;
  sizes?: { medium?: { mediaUrl?: string } };
}

const toFeedPosts = (raw: BeholdPost[]): FeedPost[] =>
  raw
    .map((p) => ({
      id: p.id,
      href: p.permalink || INSTAGRAM_URL,
      // `sizes` is always a still image, including for videos.
      image: p.sizes?.medium?.mediaUrl || p.thumbnailUrl || '',
      alt: (p.prunedCaption || 'Outpost Custom Instagram post').slice(0, 120),
    }))
    .filter((p) => p.image)
    .slice(0, 6);

const readCache = (): FeedPost[] | null => {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    return cached ? (JSON.parse(cached) as FeedPost[]) : null;
  } catch {
    return null;
  }
};

/** Black, with the downward slant the old print section had along its top edge. */
const InstagramFeed: React.FC = () => {
  const [posts, setPosts] = useState<FeedPost[]>(() => readCache() ?? fallbackPosts);

  useEffect(() => {
    // One request per browser session keeps the feed inside Behold's monthly view allowance.
    if (readCache()) return;
    let cancelled = false;
    fetch(FEED_URL)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((data: { posts?: BeholdPost[] }) => {
        const live = toFeedPosts(data.posts ?? []);
        if (cancelled || live.length === 0) return;
        setPosts(live);
        try {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify(live));
        } catch {
          // Storage can be blocked; the feed still shows for this page view.
        }
      })
      .catch(() => {
        // Keep the fallback photos.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      className="relative bg-black text-white -mt-12 pt-32 pb-20 md:pb-24 px-6"
      style={{ clipPath: 'polygon(0 0, 50% 64px, 100% 0, 100% 100%, 0 100%)' }}
    >
      <div className="max-w-6xl mx-auto">
        <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-poppins text-xs font-semibold tracking-[0.2em] uppercase text-[#78BE20] hover:text-white transition-colors mb-4"
            >
              <Instagram className="w-6 h-6" strokeWidth={2} />
              @outpostcustom
            </a>
            <h2 className="font-montserrat font-extrabold tracking-[-0.03em] leading-none text-[clamp(32px,4.5vw,56px)]">
              Behind the scenes.
            </h2>
            <p className="font-poppins text-sm md:text-base mt-4 text-white/70 max-w-xl">
              Case studies not your thing? Take a squint at our latest Instagram posts for a little look at
              our recent projects.
            </p>
          </div>
          <GreenButton href={INSTAGRAM_URL} className="self-start md:self-auto">
            Follow us on Instagram
          </GreenButton>
        </Reveal>

        <Reveal delay={100} className="mt-10 grid grid-cols-3 md:grid-cols-6 gap-2">
          {posts.map((post) => (
            <a
              key={post.id}
              href={post.href}
              target="_blank"
              rel="noopener noreferrer"
              className="block aspect-square overflow-hidden bg-white/5"
            >
              <img
                src={post.image}
                alt={post.alt}
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                loading="lazy"
              />
            </a>
          ))}
        </Reveal>
      </div>
    </section>
  );
};

export default InstagramFeed;
